"use client";

import React from "react";
import { Check, Monitor, Ruler, Weight, Zap } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const models = [{
  id: "desktop-6",
  name: "Desktop 6",
  description: "Perfect for small counters and cozy cafes.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586523/img/voltgo_desktop_6_render.png",
  capacity: "6 Powerbanks",
  dimensions: "20 x 20 x 30 cm",
  weight: "5 kg"
}, {
  id: "stand-12",
  name: "Stand 12",
  description: "Ideal for restaurants and medium-sized venues.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586523/img/voltgo_stand_12_render.png",
  capacity: "12 Powerbanks",
  dimensions: "40 x 40 x 120 cm",
  weight: "25 kg"
}, {
  id: "tower-48",
  name: "Tower 48",
  description: "High capacity for malls, events, and large spaces.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586523/img/voltgo_tower_48_render.png",
  capacity: "48 Powerbanks",
  dimensions: "60 x 60 x 180 cm",
  weight: "80 kg"
}];
const features = [{
  modelId: "desktop-6",
  text: "Compact footprint"
}, {
  modelId: "desktop-6",
  text: "Plug & play setup"
}, {
  modelId: "desktop-6",
  text: "LED status indicators"
}, {
  modelId: "stand-12",
  text: "Freestanding design"
}, {
  modelId: "stand-12",
  text: '10" Digital ad screen'
}, {
  modelId: "stand-12",
  text: "Custom branding options"
}, {
  modelId: "tower-48",
  text: "Maximum capacity"
}, {
  modelId: "tower-48",
  text: '24" HD advertising display'
}, {
  modelId: "tower-48",
  text: "Dual-sided access"
}];
const specs = [{
  modelId: "desktop-6",
  text: "Input: 110-240V AC"
}, {
  modelId: "desktop-6",
  text: "Output: 5V/2A per slot"
}, {
  modelId: "desktop-6",
  text: "Network: Wi-Fi / 4G"
}, {
  modelId: "stand-12",
  text: "Input: 110-240V AC"
}, {
  modelId: "stand-12",
  text: "Output: 5V/2A per slot"
}, {
  modelId: "stand-12",
  text: "Network: Wi-Fi / 4G / Ethernet"
}, {
  modelId: "tower-48",
  text: "Input: 110-240V AC"
}, {
  modelId: "tower-48",
  text: "Output: Fast Charge 18W"
}, {
  modelId: "tower-48",
  text: "Network: Wi-Fi / 4G / Ethernet"
}];
export default function OurModels() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="onze-modellen" className="bg-background py-24 sm:py-32 overflow-hidden" ref={ref}>
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className={`flex flex-col items-center text-center mb-16 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-4">
            Our Charging Stations
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl">
            Choose the perfect ZapGO! station for your venue. From compact desktop units to high-capacity towers with digital advertising screens.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {models.map((model, index) => {
          const modelFeatures = features.filter(f => f.modelId === model.id);
          const modelSpecs = specs.filter(s => s.modelId === model.id);
          return <div key={model.id} data-index={index} className={`transition-all duration-700 ease-out delay-${index * 100} ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
                <Card className="h-full flex flex-col border-border/50 shadow-md hover:shadow-lg transition-shadow duration-300">
                  <CardHeader className="pb-4">
                    <div className="aspect-square w-full bg-muted/30 rounded-xl mb-6 flex items-center justify-center p-6 relative overflow-hidden group">
                      <div className="absolute inset-0 bg-gradient-to-tr from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      <img src={model.image} alt={`ZapGO! ${model.name} 3D Render`} className="w-full h-full object-contain drop-shadow-xl transition-transform duration-500 group-hover:scale-105" />
                    </div>
                    <div className="flex justify-between items-start gap-4">
                      <div>
                        <CardTitle className="text-2xl mb-2">
                          {model.name}
                        </CardTitle>
                        <CardDescription className="text-sm">
                          {model.description}
                        </CardDescription>
                      </div>
                      <div className="bg-primary/10 text-primary px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap flex items-center gap-1">
                        <Zap className="size-3" />
                        {model.capacity}
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="flex-1 flex flex-col gap-6">
                    <div className="grid grid-cols-2 gap-4 py-4 border-y border-border/50">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Ruler className="size-4 text-primary" />
                        <span>{model.dimensions}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Weight className="size-4 text-primary" />
                        <span>{model.weight}</span>
                      </div>
                    </div>

                    <Tabs defaultValue="features" className="w-full flex-1">
                      <TabsList className="grid w-full grid-cols-2 mb-4">
                        <TabsTrigger value="features">Features</TabsTrigger>
                        <TabsTrigger value="specs">Tech Specs</TabsTrigger>
                      </TabsList>
                      <TabsContent value="features" className="space-y-3 mt-0 animate-in fade-in-50 duration-300">
                        {modelFeatures.map((feature, fIndex) => <div key={fIndex} data-index={fIndex} className="flex items-start gap-2">
                            <Check className="size-4 text-primary mt-0.5 shrink-0" />
                            <span className="text-sm text-foreground">
                              {feature.text}
                            </span>
                          </div>)}
                      </TabsContent>
                      <TabsContent value="specs" className="space-y-3 mt-0 animate-in fade-in-50 duration-300">
                        {modelSpecs.map((spec, sIndex) => <div key={sIndex} data-index={sIndex} className="flex items-start gap-2">
                            <Monitor className="size-4 text-muted-foreground mt-0.5 shrink-0" />
                            <span className="text-sm text-muted-foreground">
                              {spec.text}
                            </span>
                          </div>)}
                      </TabsContent>
                    </Tabs>
                  </CardContent>

                  <CardFooter className="pt-6 border-t border-border/50">
                    <Button asChild className="w-full group" size="lg">
                      <Link to="/PartnersPage">
                        Request {model.name}
                        <Zap className="ml-2 size-4 group-hover:text-accent transition-colors" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}